import React from 'react';
import { useTranslation } from 'react-i18next';
import { LocalizedDateInput } from '../../../shared/components';
import { formatLongDateLabel, toDateInputValue } from '../../../shared/utils/localization';
import { AssetTypeDefinition } from '../typeCatalog';
import styles from './AssetLayout.module.css';

type AssetTypeFieldsProps = {
  module: AssetTypeDefinition;
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
  readOnly?: boolean;
};

const toInputValue = (value: unknown, kind: string): string => {
  if (value === null || value === undefined || value === false) {
    return '';
  }
  if (kind === 'date') {
    return toDateInputValue(String(value));
  }
  if (kind === 'checkbox') {
    return '';
  }
  return String(value);
};

const parseValue = (raw: string, kind: string): unknown => {
  if (kind === 'number') {
    if (raw.trim() === '') {
      return '';
    }
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? raw : parsed;
  }
  return raw;
};

export const AssetTypeFields: React.FC<AssetTypeFieldsProps> = ({ module, values, onChange, readOnly }) => {
  const { t } = useTranslation(['assets', 'calendar', 'common']);

  const setField = (key: string, value: unknown) => {
    onChange({ ...values, [key]: value });
  };

  if (readOnly) {
    return (
      <div className={styles.detailsGrid}>
        {module.fields.map(field => {
          const raw = values[field.key];
          let display = '-';
          if (field.kind === 'checkbox') {
            display = raw === true ? t('yes', { ns: 'common', defaultValue: 'Yes' }) : t('no', { ns: 'common', defaultValue: 'No' });
          } else if (field.kind === 'select' && raw) {
            const option = field.options?.find(item => item.value === String(raw));
            display = option ? t(option.labelKey, { ns: 'assets', defaultValue: String(raw) }) : String(raw);
          } else if (raw !== null && raw !== undefined && raw !== '') {
            display = field.kind === 'date' ? (formatLongDateLabel(toDateInputValue(String(raw))) || toDateInputValue(String(raw))) : String(raw);
          }

          return (
            <div key={field.key} className={field.fullWidth ? styles.detailItemWide : undefined}>
              <p className={styles.detailLabel}>{t(field.labelKey, { ns: 'assets' })}</p>
              <p className={styles.detailValue}>{display}</p>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={styles.formGrid}>
      {module.fields.map(field => (
        <div key={field.key} className={`${styles.field} ${field.fullWidth ? styles.fieldFull : ''}`}>
          {field.kind === 'checkbox' ? (
            <label className={styles.primaryToggle}>
              <input
                type="checkbox"
                checked={values[field.key] === true}
                onChange={event => setField(field.key, event.target.checked)}
              />
              {t(field.labelKey, { ns: 'assets' })}
            </label>
          ) : (
            <>
              <label className={styles.label}>{t(field.labelKey, { ns: 'assets' })}</label>
              {field.kind === 'textarea' ? (
                <textarea
                  className={`${styles.input} ${styles.textarea}`}
                  value={toInputValue(values[field.key], field.kind)}
                  onChange={event => setField(field.key, event.target.value)}
                  rows={4}
                />
              ) : field.kind === 'select' ? (
                <select
                  className={styles.input}
                  value={toInputValue(values[field.key], field.kind)}
                  onChange={event => setField(field.key, event.target.value)}
                >
                  <option value="">{t('selectOption', { ns: 'assets' })}</option>
                  {field.options?.map(option => (
                    <option key={option.value} value={option.value}>
                      {t(option.labelKey, { ns: 'assets' })}
                    </option>
                  ))}
                </select>
              ) : field.kind === 'date' ? (
                <LocalizedDateInput
                  className={styles.input}
                  fillWidth
                  displayFormat="long"
                  value={toInputValue(values[field.key], field.kind)}
                  onValueChange={value => setField(field.key, value)}
                  emptyLabel={t('selectDate', { ns: 'calendar', defaultValue: 'Select date' })}
                />
              ) : (
                <input
                  className={styles.input}
                  type={field.kind === 'number' ? 'number' : 'text'}
                  value={toInputValue(values[field.key], field.kind)}
                  onChange={event => setField(field.key, parseValue(event.target.value, field.kind))}
                />
              )}
            </>
          )}
        </div>
      ))}
    </div>
  );
};

export default AssetTypeFields;
